/**
 * THE ONE MESSAGE FORMATTER — the ICU subset the catalogs are written in, resolved
 * in exactly one place.
 *
 * It exists because there were two: `translate()` inside the React tree and
 * `moduleTranslate()` outside it each carried their own copy of the same regex, and
 * they had already drifted — the module one silently dropped `#`, so every count in
 * a message it rendered vanished. A catalog entry is one string whichever caller
 * reaches it, so the resolution of that string cannot be a property of the caller.
 *
 * ── A PLURAL BRANCH IS NOT FLAT ──────────────────────────────────────────────
 *
 * The branch pattern used to be `[^{}]*`, which says "a branch contains no braces".
 * That is false of the messages we actually write: the reference-list brief and the
 * set-aside line both name things inside their branches (`{selected}`,
 * `{statements}`), because German and English put the plural agreement and the named
 * list in the same sentence. A message like that could never match, so the whole
 * `{n, plural, …}` block reached the screen VERBATIM — the reader was shown the ICU
 * source of the sentence meant for them.
 *
 * `BRANCH` therefore admits one level of nesting: any non-brace run, or a balanced
 * `{…}` group. One level is enough for every catalog entry and it keeps the pattern
 * unable to run past its own closing brace — a lone `}` matches neither alternative,
 * so a branch can never swallow the plural that follows it.
 *
 * ── TWO PHASES, NOT ONE PASS PER KEY ─────────────────────────────────────────
 *
 * Plurals for EVERY key are resolved first, and only then are the plain `{key}`
 * tokens replaced. Interleaving them per key (the previous shape) made the outcome
 * depend on `Object.entries` order: `{statements}` replaced before `{n}`'s plural was
 * chosen would inject the label list into a branch that was still unselected, and
 * whether that happened came down to the order the caller happened to build its
 * argument object in. Two phases make the argument object an unordered bag again.
 */

/** One plural branch: non-brace text and balanced single-level `{…}` groups. */
const BRANCH = "((?:[^{}]|\\{[^{}]*\\})*)"

const escapeForRegExp = (key: string) =>
  key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

export function formatTranslation(
  template: string,
  dynamicValues?: Record<string, string | number>,
): string {
  if (!dynamicValues) return template

  const entries = Object.entries(dynamicValues)
  let out = template

  // PHASE 1 — choose a branch for every plural block.
  for (const [key, value] of entries) {
    const pluralPattern = new RegExp(
      `\\{${escapeForRegExp(key)}, plural, one \\{${BRANCH}\\} other \\{${BRANCH}\\}\\}`,
      "g",
    )
    out = out.replace(pluralPattern, (_, one: string, other: string) =>
      // ICU: inside a plural branch, `#` is the argument's own value. A function
      // replacer, so a `$&` or `$1` in the value stays literal text.
      (Number(value) === 1 ? one : other).replaceAll("#", () => String(value)),
    )
  }

  // PHASE 2 — plain placeholders, including the ones a chosen branch just exposed.
  for (const [key, value] of entries) {
    out = out.replaceAll(`{${key}}`, () => String(value))
  }

  return out
}
